import React, { Suspense } from "react";
// import SubjectsSection from "@/pages/courses/SubjectsSection.js";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Mission from "@/pages/home/Mission.jsx";
import Team from "@/pages/home/Team.jsx";
import Partners from "@/pages/home/Partners.jsx";
import Contact from "@/pages/home/ContactUs";
import QuizContainer from "@/pages/courses/QuizContainer.js";
// import CoursesSection from "@/pages/courses/Courses.js";
import * as Sentry from "@sentry/react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CourseRedirect from "@/pages/courses/CourseRedirect.js";
import SentryErrorBoundary from "./config/SentryErrorBoundary.js";
import LessonWrapper from "@/pages/lesson/LessonWrapper.js";
import { PageSkeleton } from "./config/utils";
import Dashboard from "@/pages/dashboard/index";
import DashboardHome from "@/pages/dashboard/home";
import { AuthProvider } from "./components/dashboard/contexts/auth-context";
import CoursesPage from "@/pages/dashboard/courses";
import CoursePage from "@/pages/dashboard/courses/[courseId]/index";
import ProgressPage from "@/pages/dashboard/progress";
import AssignmentsPage from "@/pages/dashboard/assignments";
import AssignmentPage from "@/pages/dashboard/assignments/[assignmentId]";
import AchievementsPage from "@/pages/dashboard/achievements";
import ProfilePage from "@/pages/profile";
import { ThemeProvider } from "./components/ThemeContext";
import SettingsPage from "@/pages/dashboard/settings";
import MainLayout from "./components/MainLayout";
import "./assets/css/Globals.css";
import LessonPage from "@/pages/dashboard/courses/[courseId]/lessons/[lessonId]/index.jsx";
import CourseNotesPage from "@/pages/dashboard/courses/[courseId]/notes/index.jsx";
import CourseQuizPage from "@/pages/dashboard/courses/[courseId]/quiz/index.jsx";
import LessonQuizzesPage from "@/pages/dashboard/courses/[courseId]/lessons/[lessonId]/quizzes/index.jsx";
import LessonNotesPage from "@/pages/dashboard/courses/[courseId]/lessons/[lessonId]/notes/index.jsx";
import CourseMaterialsPage from "@/pages/dashboard/courses/[courseId]/materials/index.jsx";
import FlashcardsPage from "@/pages/dashboard/courses/[courseId]/flashcards/index.jsx";
import SubscriptionPage from "@/pages/subscription/index.jsx";
import CheckoutPage from "@/pages/subscription/checkout/[planId]/index.jsx";
import FlashcardStudyPage from "@/pages/dashboard/courses/[courseId]/flashcards/[setId]/index.jsx";
import Home from "@/pages/index.jsx";
import LoginPage from "@/pages/auth/login/index.jsx";
import RegisterPage from "@/pages/auth/register/index.jsx";
import ForgotPasswordPage from "@/pages/auth/forgot-password/index.jsx";
import ResetPasswordPage from "@/pages/auth/reset-password/[token]/index.jsx";
import SuccessPage from "@/pages/auth/success/index.jsx";
import VerifyEmailInstructionsPage from "@/pages/auth/verify-email/index.jsx";
import VerifyEmailPage from "@/pages/auth/verify-email/[token]/index.jsx";
import LessonFlashcardsPage from "@/pages/dashboard/courses/[courseId]/lessons/[lessonId]/flashcards/index.jsx";
import BenchmarkTest from "./pages/misc/benchmark-test.jsx";
import Courses from "./pages/courses/index.jsx";
import ComingSoon from "./pages/coming-soon.jsx";

Sentry.init({
	dsn: "https://7e5589f837c1848a7a7486d7bd8a64f8@o4508677879824384.ingest.us.sentry.io/4508677888016384",
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.replayIntegration(),
	],
	tracesSampleRate: 0.2,
	replaysSessionSampleRate: 0.05,
	replaysOnErrorSampleRate: 0.2,
	environment: process.env.NODE_ENV,
	beforeSend(event) {
		if (process.env.NODE_ENV === "development") {
			return null;
		}
		return event;
	},
});

function App() {
	return (
		<SentryErrorBoundary>
			<Router>
				<ThemeProvider>
					<AuthProvider>
						<div className="app">
							<Suspense fallback={<PageSkeleton />}>
								<Routes>
									{/* Landing page  */}
									<Route element={<MainLayout />}>
										<Route
											path="/mission"
											element={<Mission />}
										/>
										<Route
											path="/team"
											element={<Team />}
										/>
										<Route
											path="/partners"
											element={<Partners />}
										/>
										<Route
											path="/contact"
											element={<Contact />}
										/>
										<Route
											path="/courses"
											element={<Courses />}
										/>

										<Route
											path="/courses/:subjectSlug/:courseSlug/:unitSlug/:lessonSlug/:lessonId/quiz"
											element={<QuizContainer />}
										/>
										<Route
											path="/courses/:subjectSlug/:courseSlug"
											element={<CourseRedirect />}
										/>
										<Route
											path="/courses/:subjectSlug/:courseSlug/:unitSlug/:lessonSlug/:lessonId"
											element={<LessonWrapper />}
										/>

										<Route
											path="/profile"
											element={<ProfilePage />}
										/>

										<Route path="/" element={<Home />} />

										<Route
											path="/subscription"
											element={<SubscriptionPage />}
										/>

										<Route
											path="/subscription/checkout/:planId"
											element={<CheckoutPage />}
										/>

										<Route
											path="/benchmark-test"
											element={<BenchmarkTest />}
										/>
									</Route>

									{/* Authentication Pages */}
									<Route>
										<Route
											path="/auth/login"
											element={<ComingSoon />}
										/>
										<Route
											path="/auth/register"
											element={<ComingSoon />}
										/>
										<Route
											path="/auth/forgot-password"
											element={<ComingSoon />}
										/>
										<Route
											path="/auth/reset-password"
											element={<ComingSoon />}
										/>
										<Route
											path="/auth/verify-email"
											element={<ComingSoon />}
										/>
										<Route
											path="/auth/verify-email/:token"
											element={<ComingSoon />}
										/>
										<Route
											path="/auth/success"
											element={<ComingSoon />}
										/>
									</Route>

									{/* Student Dashboard */}
									<Route
										path="/dashboard"
										element={<Dashboard />}>
										<Route
											path="/dashboard"
											element={<DashboardHome />}
										/>
										<Route
											path="/dashboard/courses"
											element={<CoursesPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/materials"
											element={<CourseMaterialsPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/flashcards"
											element={<FlashcardsPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/quiz"
											element={<CourseQuizPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId"
											element={<CoursePage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/lessons/:lessonId"
											element={<LessonPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/lessons/:lessonId/notes"
											element={<LessonNotesPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/notes"
											element={<CourseNotesPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/flashcards/:setId"
											element={<FlashcardStudyPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/lessons/:lessonId/quizzes"
											element={<LessonQuizzesPage />}
										/>

										<Route
											path="/dashboard/courses/:courseId/lessons/:lessonId/flashcards"
											element={<LessonFlashcardsPage />}
										/>

										<Route
											path="/dashboard/progress"
											element={<ProgressPage />}
										/>
										<Route
											path="/dashboard/assignments"
											element={<AssignmentsPage />}
										/>
										<Route
											path="/dashboard/assignments/:assignmentId"
											element={<AssignmentPage />}
										/>
										<Route
											path="/dashboard/achievements"
											element={<AchievementsPage />}
										/>
										<Route
											path="/dashboard/settings"
											element={<SettingsPage />}
										/>
									</Route>
								</Routes>
							</Suspense>
							<ToastContainer />
						</div>
					</AuthProvider>
				</ThemeProvider>
			</Router>
		</SentryErrorBoundary>
	);
}

export default App;
